import { MAX_UNIT_PRICE } from '@repo/api-contracts';
import { type ReactElement, useEffect, useState } from 'react';
import { Input } from '@/shared/ui';

interface UnitPriceFilterProps {
  readonly min: number | undefined;
  readonly max: number | undefined;
  readonly onChange: (range: {
    readonly min: number | undefined;
    readonly max: number | undefined;
  }) => void;
}

const toDraft = (value: number | undefined): string => value?.toString() ?? '';

const parseWon = (draft: string): number | undefined => {
  const parsed = Number(draft);
  return Number.isInteger(parsed) && parsed >= 0 && parsed <= MAX_UNIT_PRICE && draft !== ''
    ? parsed
    : undefined;
};

/** 1개당 단가 범위(원) — blur/Enter 에 확정한다. */
export const UnitPriceFilter = ({ min, max, onChange }: UnitPriceFilterProps): ReactElement => {
  const [minDraft, setMinDraft] = useState(toDraft(min));
  const [maxDraft, setMaxDraft] = useState(toDraft(max));

  useEffect(() => {
    setMinDraft(toDraft(min));
  }, [min]);
  useEffect(() => {
    setMaxDraft(toDraft(max));
  }, [max]);

  const commit = (): void => {
    const parsedMin = parseWon(minDraft);
    const parsedMax = parseWon(maxDraft);
    // 역전된 범위(최소 > 최대)는 의도를 살려 교환한다 — 그대로 두면 항상 빈 목록이 된다.
    const isInverted = parsedMin !== undefined && parsedMax !== undefined && parsedMin > parsedMax;
    const nextMin = isInverted ? parsedMax : parsedMin;
    const nextMax = isInverted ? parsedMin : parsedMax;

    // 무효 입력은 표시도 함께 정리한다 — 적용된 것처럼 입력창에 남지 않게.
    setMinDraft(toDraft(nextMin));
    setMaxDraft(toDraft(nextMax));

    // 값이 그대로면 커밋하지 않는다 — blur 마다 동일 URL 을 히스토리에 쌓지 않게.
    if (nextMin !== min || nextMax !== max) {
      onChange({ min: nextMin, max: nextMax });
    }
  };

  const commitOnEnter = (event: { readonly key: string }): void => {
    if (event.key === 'Enter') {
      commit();
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2">
        <Input
          aria-label="단가 최소"
          inputMode="numeric"
          placeholder="최소"
          value={minDraft}
          onChange={(event) => setMinDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={commitOnEnter}
        />
        <span className="text-muted-foreground">~</span>
        <Input
          aria-label="단가 최대"
          inputMode="numeric"
          placeholder="최대"
          value={maxDraft}
          onChange={(event) => setMaxDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={commitOnEnter}
        />
      </div>
    </div>
  );
};
